import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Auth() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/discover";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name };
      const { data } = await api.post(endpoint, payload);
      setUser(data.user);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate(data.user.onboarded ? "/discover" : "/onboarding", { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-5 h-20 flex items-center"><Logo size="text-3xl" /></header>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md glass rounded-3xl p-8 fade-up">
          <h1 className="font-display font-black text-3xl">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-emerald-100/60 mt-2">
            {mode === "login" ? "Pick up where you left off." : "Date around real events in minutes."}
          </p>

          <Button data-testid="google-login-btn" onClick={googleLogin} type="button"
            className="w-full mt-6 h-12 rounded-full bg-white text-gray-900 hover:bg-gray-100 font-semibold flex items-center justify-center gap-3">
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.27-4.74 3.27-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-emerald-100/40 uppercase tracking-widest">or email</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-emerald-100/70">Name</Label>
                <Input data-testid="auth-name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 h-11 rounded-xl" placeholder="Alex Doe" />
              </div>
            )}
            <div>
              <Label className="text-emerald-100/70">Email</Label>
              <Input data-testid="auth-email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-white/5 border-white/10 h-11 rounded-xl" placeholder="you@email.com" />
            </div>
            <div>
              <Label className="text-emerald-100/70">Password</Label>
              <Input data-testid="auth-password" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 bg-white/5 border-white/10 h-11 rounded-xl" placeholder="••••••••" />
            </div>
            <Button data-testid="auth-submit" disabled={busy} type="submit" className="w-full btn-gold h-12 rounded-full text-base">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="text-center text-sm text-emerald-100/60 mt-6">
            {mode === "login" ? "New to MoYo Stars Date?" : "Already have an account?"}{" "}
            <button data-testid="auth-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-amber-400 font-semibold hover:text-amber-300">
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
